import React, { useEffect, lazy, Suspense } from "react";
import { Routes, Route, useNavigate, useParams, useLocation, Navigate } from "react-router-dom";
import { motion } from "motion/react";
import { useAuth, useApp, useGame } from "../contexts";
import { ROUTES, routeToTab } from "./routeConfig";
import { subscribeCourts } from "../utils/firebase";
import { CourtItem } from "../types/userProfile";
import { useCommercialEntitlement } from "../hooks/useCommercialEntitlement";
import { OperationalModuleBlockedView } from "../components/commercial/OperationalModuleBlockedView";

// Lazy loading das visões de rota para Code Splitting e menor Bundle Inicial
const SportsHub = lazy(() => import("../components/hub/SportsHub"));
const GamePanel = lazy(() => import("../components/GamePanel"));
const InitialSetup = lazy(() => import("../components/InitialSetup"));
const CourtBookingHub = lazy(() => import("../components/courts/CourtBookingHub"));
const TorneioManager = lazy(() => import("../components/TorneioManager"));
const PublicPortal = lazy(() => import("../components/PublicPortal"));
const MatchHistory = lazy(() => import("../components/MatchHistory"));
const UserProfileManager = lazy(() => import("../components/profile/UserProfileManager"));
const StatsPanel = lazy(() => import("../components/StatsPanel"));
const VendasFinanceiroPanel = lazy(() => import("../components/courts/VendasFinanceiroPanel"));
const DayUseDuesPanel = lazy(() => import("../components/courts/DayUseDuesPanel"));
const MensalistasFixedPanel = lazy(() => import("../components/courts/MensalistasFixedPanel"));
const HorariosEventosHub = lazy(() => import("../components/courts/HorariosEventosHub"));
const ArenaFinanceiroHub = lazy(() => import("../components/courts/ArenaFinanceiroHub"));
const AthleteBookingsHistory = lazy(() =>
  import("../components/athlete/AthleteBookingsHistory").then((m) => ({
    default: m.AthleteBookingsHistory,
  }))
);
const AdminPanel = lazy(() => import("../components/AdminPanel"));
const SuperAdminLoginGate = lazy(() => import("../components/auth/SuperAdminLoginGate"));
const TournamentAssistantInvite = lazy(() => import("../components/tournaments/TournamentAssistantInvite").then(m => ({ default: m.TournamentAssistantInvite })));
const TournamentAssistantArea = lazy(() => import("../components/tournaments/TournamentAssistantArea").then(m => ({ default: m.TournamentAssistantArea })));
const HubEsportivoLandingPage = lazy(() => import("../components/public/HubEsportivoLandingPage"));

/**
 * Loading fallback para transição suave entre rotas
 */
function PageLoader() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] p-8 space-y-4">
      <div className="w-10 h-10 border-4 border-amber-400/20 border-t-amber-400 rounded-full animate-spin" />
      <p className="text-xs font-bold text-zinc-400 tracking-wider uppercase animate-pulse">
        Carregando...
      </p>
    </div>
  );
}

/**
 * Route wrappers for deep link parameters
 */
function TournamentRouteWrapper(props: {
  duplas: any[];
  torneios: any[];
  handleSaveTorneio: any;
  handleDeleteTorneio: any;
  user: any;
  userProfile: any;
  setUserProfile: any;
  handleAddDupla: any;
  isLoggedIn: boolean;
  setShowGoogleAuthModal: any;
}) {
  const { tournamentId } = useParams();
  
  useEffect(() => {
    if (tournamentId) {
      sessionStorage.setItem("public_portal_torneio_id", tournamentId);
    }
  }, [tournamentId]);

  return (
    <PublicPortal
      tournamentId={tournamentId}
      duplas={props.duplas}
      torneios={props.torneios}
      onSaveTorneio={props.handleSaveTorneio}
      onAddDupla={props.handleAddDupla}
      userId={props.user?.uid || null}
      isLoggedIn={props.isLoggedIn}
      onOpenGoogleAuth={() => props.setShowGoogleAuthModal(true)}
    />
  );
}

function AdminRouteWrapper(props: {
  torneios: any[];
  duplas: any[];
  handleSaveTorneio: any;
  handleDeleteTorneio: any;
  handleAddDupla: any;
  isSuperAdminAuth: boolean;
  setIsSuperAdminAuth: (val: boolean) => void;
  setAdminSubTab: (subtab: string) => void;
}) {
  const { subtab } = useParams();

  useEffect(() => {
    if (subtab) {
      props.setAdminSubTab(subtab);
    }
  }, [subtab]);

  if (!props.isSuperAdminAuth) {
    return <SuperAdminLoginGate onAuthenticated={() => props.setIsSuperAdminAuth(true)} />;
  }

  return (
    <AdminPanel
      torneios={props.torneios}
      duplas={props.duplas}
      onSaveTorneio={props.handleSaveTorneio}
      onDeleteTorneio={props.handleDeleteTorneio}
      onAddDupla={props.handleAddDupla}
      adminTab={(subtab as any) || ""}
      onSelectAdminTab={props.setAdminSubTab}
    />
  );
}

export const AppRoutes: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    user,
    userProfile,
    isLoggedIn,
    showGoogleAuthModal,
    setShowGoogleAuthModal,
    openAuthModalWithRole,
    setUserProfile,
    handleGoogleLogout,
  } = useAuth();

  // Pending Invite Recovery
  useEffect(() => {
    if (user && location.pathname !== "/convite-ajudante") {
      const pending = sessionStorage.getItem("pendingAssistantInvite");
      if (pending) {
        try {
          const { tournamentId, token } = JSON.parse(pending);
          if (tournamentId && token) {
             navigate(`/convite-ajudante?tournamentId=${tournamentId}&token=${token}`);
          }
        } catch(e) {}
      }
    }
  }, [user, location.pathname, navigate]);

  const {
    activeTab,
    handleSelectTab,
    adminSubTab,
    setAdminSubTab,
    isSuperAdminAuth,
    setIsSuperAdminAuth,
    setShowDownloadAppModal,
    showToast,
    rachaHostId,
    handleExitCoOrganizing,
  } = useApp();

  const {
    duplas,
    globalDuplas,
    gameState,
    setGameState,
    historico,
    torneios,
    sessoes,
    getEffectiveCloudUid,
    handleAddDupla,
    handleEditDupla,
    handleDeleteDupla,
    handleDuplicateDupla,
    handleApplySorteio,
    handleStartRachao,
    handleEndRachao,
    handleReactivateSession,
    handleDeleteSession,
    handleClearAllSessions,
    handleConfirmRound,
    handleUpdateQueue,
    handleUpdateDuplas,
    handleUpdateGameState,
    handleSaveTorneio,
    handleDeleteTorneio,
    handleClearHistory,
    exportJSON,
    exportExcel,
  } = useGame();

  const [courts, setCourts] = React.useState<CourtItem[]>([]);

  const handleSmartBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      handleSelectTab("hub");
    }
  };

  const {
    entitlement,
    canAccessOperational,
    canonicalArenaId,
    arenaData,
  } = useCommercialEntitlement({
    userProfile,
    courts,
  });

  useEffect(() => {
    const unsub = subscribeCourts(
      (list) => {
        setCourts(list);
      },
      [],
      {
        userUid: user?.uid || (userProfile?.id !== "guest_visitor" ? userProfile?.id : undefined),
        userEmail: user?.email || userProfile?.email,
        accountType: userProfile?.accountType || "atleta",
        role: userProfile?.role || "atleta",
        isArenaOwner: userProfile?.accountType === "dono_arena" || userProfile?.isArenaOwner === true,
      }
    );
    return () => unsub();
  }, [user?.uid, user?.email, userProfile?.accountType, userProfile?.role, userProfile?.id, userProfile?.isArenaOwner]);

  // Keep activeTab in sync with current URL path when user navigates using browser back/forward or direct URL input
  useEffect(() => {
    const { tab, subtab } = routeToTab(location.pathname);
    if (tab !== activeTab) {
      handleSelectTab(tab, false); // false = do not re-push history
    }
    if (subtab && subtab !== adminSubTab) {
      setAdminSubTab(subtab);
    }
  }, [location.pathname]);

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
      <Route path="/convite-ajudante" element={<TournamentAssistantInvite />} />
      {/* LANDING PAGE INSTITUCIONAL */}
      <Route
        path={ROUTES.LANDING}
        element={
          <HubEsportivoLandingPage
            onOpenGoogleAuth={(role) => openAuthModalWithRole(role)}
            onGoToHub={() => {
              handleSelectTab("hub");
              navigate(ROUTES.HUB);
            }}
            isLoggedIn={isLoggedIn}
            userProfile={userProfile}
          />
        }
      />
      {/* HUB PRINCIPAL */}
      <Route
        path={ROUTES.HUB}
        element={
          <motion.div
            key="hub"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            <SportsHub
              userProfile={userProfile}
              isLoggedIn={isLoggedIn}
              activeTab={activeTab}
              onSelectTab={(tab) => handleSelectTab(tab)}
              onOpenGoogleAuth={() => setShowGoogleAuthModal(true)}
              onOpenDownloadApp={() => setShowDownloadAppModal(true)}
            />
          </motion.div>
        }
      />
      <Route
        path={ROUTES.HUB_ALIAS}
        element={
          <motion.div
            key="hub"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            <SportsHub
              userProfile={userProfile}
              isLoggedIn={isLoggedIn}
              activeTab={activeTab}
              onSelectTab={(tab) => handleSelectTab(tab)}
              onOpenGoogleAuth={() => setShowGoogleAuthModal(true)}
              onOpenDownloadApp={() => setShowDownloadAppModal(true)}
            />
          </motion.div>
        }
      />

      {/* JOGO / RACHA */}
      <Route
        path={ROUTES.JOGO}
        element={
          <motion.div
            key="game"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            {gameState.emAndamento ? (
              <GamePanel
                gameState={gameState}
                duplas={globalDuplas}
                onConfirmRound={handleConfirmRound}
                onEndRachao={handleEndRachao}
                onUpdateQueue={handleUpdateQueue}
                onUpdateDuplas={handleUpdateDuplas}
                onUpdateGameState={handleUpdateGameState}
                userId={getEffectiveCloudUid()}
                rachaHostId={rachaHostId}
                onExitCoOrganizing={handleExitCoOrganizing}
              />
            ) : (
              <InitialSetup
                duplas={globalDuplas}
                onStartRachao={handleStartRachao}
                regras={
                  gameState.regras || {
                    tamanhoEquipe: 2,
                    modoPlay: "solo",
                    limiteVitorias: 0,
                    destinoVencedorLimite: "primeira_proxima",
                    nomenclaturaFusao: "misto",
                    esquemaRotacao: "desafiante",
                  }
                }
                onUpdateRegras={(novasRegras) => {
                  const novoState = { ...gameState, regras: novasRegras };
                  setGameState(novoState);
                }}
                onApplySorteio={handleApplySorteio}
                onAddDupla={handleAddDupla}
              />
            )}
          </motion.div>
        }
      />
      <Route
        path={ROUTES.RACHA}
        element={
          <motion.div
            key="racha"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            {gameState.emAndamento ? (
              <GamePanel
                gameState={gameState}
                duplas={globalDuplas}
                onConfirmRound={handleConfirmRound}
                onEndRachao={handleEndRachao}
                onUpdateQueue={handleUpdateQueue}
                onUpdateDuplas={handleUpdateDuplas}
                onUpdateGameState={handleUpdateGameState}
                userId={getEffectiveCloudUid()}
                rachaHostId={rachaHostId}
                onExitCoOrganizing={handleExitCoOrganizing}
              />
            ) : (
              <InitialSetup
                duplas={globalDuplas}
                onStartRachao={handleStartRachao}
                regras={
                  gameState.regras || {
                    tamanhoEquipe: 2,
                    modoPlay: "solo",
                    limiteVitorias: 0,
                    destinoVencedorLimite: "primeira_proxima",
                    nomenclaturaFusao: "misto",
                    esquemaRotacao: "desafiante",
                  }
                }
                onUpdateRegras={(novasRegras) => {
                  const novoState = { ...gameState, regras: novasRegras };
                  setGameState(novoState);
                }}
                onApplySorteio={handleApplySorteio}
                onAddDupla={handleAddDupla}
              />
            )}
          </motion.div>
        }
      />

      {/* QUADRAS & DEEP LINK ARENA */}
      <Route
        path={ROUTES.QUADRAS}
        element={
          <motion.div
            key="quadras"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            <CourtBookingHub
              userProfile={userProfile}
              onUpdateProfile={(updated) => setUserProfile(updated)}
              isLoggedIn={isLoggedIn}
              onOpenGoogleAuth={() => setShowGoogleAuthModal(true)}
              onBack={handleSmartBack}
              onNavigateTab={(tab) => handleSelectTab(tab as any)}
            />
          </motion.div>
        }
      />
      <Route
        path={ROUTES.ARENA_QUADRAS}
        element={
          <motion.div
            key="arena_quadras"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            <CourtBookingHub
              userProfile={userProfile}
              onUpdateProfile={(updated) => setUserProfile(updated)}
              isLoggedIn={isLoggedIn}
              onOpenGoogleAuth={() => setShowGoogleAuthModal(true)}
              onBack={handleSmartBack}
            />
          </motion.div>
        }
      />
      <Route
        path={ROUTES.ARENA_DETAILS}
        element={
          <motion.div
            key="arena_details"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            <CourtBookingHub
              userProfile={userProfile}
              onUpdateProfile={(updated) => setUserProfile(updated)}
              isLoggedIn={isLoggedIn}
              onOpenGoogleAuth={() => setShowGoogleAuthModal(true)}
              onBack={handleSmartBack}
            />
          </motion.div>
        }
      />

      {/* MÓDULO: HORÁRIOS & EVENTOS (DAY USE LIVRE E HORÁRIO FIXO) */}
      <Route
        path={ROUTES.HORARIOS_EVENTOS}
        element={
          <motion.div
            key="horarios_eventos"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            <HorariosEventosHub
              userProfile={userProfile}
              courts={courts}
              initialSubTab="dayuse_livre"
              onToast={showToast}
              onBack={() => handleSelectTab("hub")}
            />
          </motion.div>
        }
      />
      <Route
        path={ROUTES.ARENA_HORARIOS_EVENTOS}
        element={
          <motion.div
            key="arena_horarios_eventos"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            <HorariosEventosHub
              userProfile={userProfile}
              courts={courts}
              initialSubTab="dayuse_livre"
              onToast={showToast}
              onBack={() => handleSelectTab("hub")}
            />
          </motion.div>
        }
      />
      <Route
        path={ROUTES.DAYUSE}
        element={
          <motion.div
            key="dayuse"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            <HorariosEventosHub
              userProfile={userProfile}
              courts={courts}
              initialSubTab="dayuse_livre"
              onToast={showToast}
              onBack={() => handleSelectTab("hub")}
            />
          </motion.div>
        }
      />
      <Route
        path={ROUTES.ARENA_DAYUSE}
        element={
          <motion.div
            key="arena_dayuse"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            <HorariosEventosHub
              userProfile={userProfile}
              courts={courts}
              initialSubTab="dayuse_livre"
              onToast={showToast}
              onBack={() => handleSelectTab("hub")}
            />
          </motion.div>
        }
      />

      {/* ROTAS DE HORÁRIOS FIXOS / MENSALISTAS (INTEGRADOS DENTRO DE HORÁRIOS & EVENTOS) */}
      <Route
        path={ROUTES.HORARIO_FIXO}
        element={
          <motion.div
            key="horario_fixo"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            <HorariosEventosHub
              userProfile={userProfile}
              courts={courts}
              initialSubTab="horario_fixo"
              onToast={showToast}
              onBack={() => handleSelectTab("dayuse")}
            />
          </motion.div>
        }
      />
      <Route
        path={ROUTES.ARENA_HORARIO_FIXO}
        element={
          <motion.div
            key="arena_horario_fixo"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            <HorariosEventosHub
              userProfile={userProfile}
              courts={courts}
              initialSubTab="horario_fixo"
              onToast={showToast}
              onBack={() => handleSelectTab("dayuse")}
            />
          </motion.div>
        }
      />
      <Route
        path={ROUTES.MENSALISTAS}
        element={
          <motion.div
            key="mensalistas"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            <HorariosEventosHub
              userProfile={userProfile}
              courts={courts}
              initialSubTab="horario_fixo"
              onToast={showToast}
              onBack={() => handleSelectTab("dayuse")}
            />
          </motion.div>
        }
      />
      <Route
        path={ROUTES.ARENA_MENSALISTAS}
        element={
          <motion.div
            key="arena_mensalistas"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            <HorariosEventosHub
              userProfile={userProfile}
              courts={courts}
              initialSubTab="horario_fixo"
              onToast={showToast}
              onBack={() => handleSelectTab("dayuse")}
            />
          </motion.div>
        }
      />

      {/* MÓDULO: FINANCEIRO CONSOLIDADO DA ARENA */}
      <Route
        path={ROUTES.FINANCEIRO}
        element={
          <motion.div
            key="financeiro"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            {userProfile.accountType === "dono_arena" ? (
              !canAccessOperational ? (
                <OperationalModuleBlockedView
                  arenaName={arenaData?.name || arenaData?.arenaName || userProfile.arenaName || "Sua Arena"}
                  arenaId={canonicalArenaId}
                  ownerUid={user?.uid || userProfile.id}
                  onBack={() => handleSelectTab("hub")}
                  onToast={showToast}
                />
              ) : (
                <ArenaFinanceiroHub
                  userProfile={userProfile}
                  courts={courts}
                  onToast={showToast}
                />
              )
            ) : (
              <div className="p-8 text-center bg-zinc-950 border border-zinc-800 rounded-3xl max-w-lg mx-auto my-12 space-y-4">
                <div className="w-16 h-16 bg-amber-400/10 text-amber-400 rounded-full flex items-center justify-center mx-auto font-bold text-2xl border border-amber-400/20">
                  🔒
                </div>
                <h3 className="text-xl font-display font-black text-white">
                  Acesso Exclusivo para Donos de Arena
                </h3>
                <p className="text-xs text-zinc-400">
                  O painel Financeiro é exclusivo para gestores de arenas cadastradas.
                </p>
              </div>
            )}
          </motion.div>
        }
      />
      <Route
        path={ROUTES.ARENA_FINANCEIRO}
        element={
          <motion.div
            key="arena_financeiro"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            {userProfile.accountType === "dono_arena" ? (
              !canAccessOperational ? (
                <OperationalModuleBlockedView
                  arenaName={arenaData?.name || arenaData?.arenaName || userProfile.arenaName || "Sua Arena"}
                  arenaId={canonicalArenaId}
                  ownerUid={user?.uid || userProfile.id}
                  onBack={() => handleSelectTab("hub")}
                  onToast={showToast}
                />
              ) : (
                <ArenaFinanceiroHub
                  userProfile={userProfile}
                  courts={courts}
                  onToast={showToast}
                />
              )
            ) : (
              <div className="p-8 text-center bg-zinc-950 border border-zinc-800 rounded-3xl max-w-lg mx-auto my-12 space-y-4">
                <div className="w-16 h-16 bg-amber-400/10 text-amber-400 rounded-full flex items-center justify-center mx-auto font-bold text-2xl border border-amber-400/20">
                  🔒
                </div>
                <h3 className="text-xl font-display font-black text-white">
                  Acesso Exclusivo para Donos de Arena
                </h3>
                <p className="text-xs text-zinc-400">
                  O painel Financeiro é exclusivo para gestores de arenas cadastradas.
                </p>
              </div>
            )}
          </motion.div>
        }
      />

      {/* SOLICITAÇÕES DE TORNEIOS PARA A ARENA */}
      <Route
        path={ROUTES.ARENA_TORNEIOS}
        element={
          <motion.div
            key="arena_torneios"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            <CourtBookingHub
              userProfile={userProfile}
              onUpdateProfile={(updated) => setUserProfile(updated)}
              isLoggedIn={isLoggedIn}
              onOpenGoogleAuth={() => setShowGoogleAuthModal(true)}
              onBack={() => handleSelectTab("hub")}
              initialTab="torneios"
            />
          </motion.div>
        }
      />

      {/* VENDAS & PAINEL DO DONO DE ARENA */}
      <Route
        path={ROUTES.VENDAS}
        element={
          <motion.div
            key="vendas"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            {userProfile.accountType === "dono_arena" ? (
              !canAccessOperational ? (
                <OperationalModuleBlockedView
                  arenaName={arenaData?.name || arenaData?.arenaName || userProfile.arenaName || "Sua Arena"}
                  arenaId={canonicalArenaId}
                  ownerUid={user?.uid || userProfile.id}
                  onBack={() => handleSelectTab("hub")}
                  onToast={showToast}
                />
              ) : (
                <VendasFinanceiroPanel userProfile={userProfile} courts={courts} onToast={showToast} />
              )
            ) : (
              <div className="p-8 text-center bg-zinc-950 border border-zinc-800 rounded-3xl max-w-lg mx-auto my-12 space-y-4">
                <div className="w-16 h-16 bg-amber-400/10 text-amber-400 rounded-full flex items-center justify-center mx-auto font-bold text-2xl border border-amber-400/20">
                  🔒
                </div>
                <h3 className="text-xl font-display font-black text-white">
                  Acesso Exclusivo para Donos de Arena
                </h3>
                <p className="text-xs text-zinc-400">
                  O painel de Vendas, Estoque e Relatórios Financeiros é exclusivo para gestores de arenas. Se você
                  possui uma arena, altere seu tipo de conta no seu Perfil.
                </p>
                <button
                  type="button"
                  onClick={() => handleSelectTab("perfil")}
                  className="px-5 py-2.5 bg-amber-400 text-emerald-950 font-black text-xs rounded-xl shadow-lg transition hover:bg-amber-300 cursor-pointer"
                >
                  Ir para Meu Perfil / Alterar Tipo de Conta
                </button>
              </div>
            )}
          </motion.div>
        }
      />
      <Route
        path={ROUTES.ARENA_DASHBOARD}
        element={
          <motion.div
            key="arena_dashboard"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            {userProfile.accountType === "dono_arena" ? (
              !canAccessOperational ? (
                <OperationalModuleBlockedView
                  arenaName={arenaData?.name || arenaData?.arenaName || userProfile.arenaName || "Sua Arena"}
                  arenaId={canonicalArenaId}
                  ownerUid={user?.uid || userProfile.id}
                  onBack={() => handleSelectTab("hub")}
                  onToast={showToast}
                />
              ) : (
                <VendasFinanceiroPanel userProfile={userProfile} courts={courts} onToast={showToast} />
              )
            ) : (
              <div className="p-8 text-center bg-zinc-950 border border-zinc-800 rounded-3xl max-w-lg mx-auto my-12 space-y-4">
                <div className="w-16 h-16 bg-amber-400/10 text-amber-400 rounded-full flex items-center justify-center mx-auto font-bold text-2xl border border-amber-400/20">
                  🔒
                </div>
                <h3 className="text-xl font-display font-black text-white">
                  Acesso Exclusivo para Donos de Arena
                </h3>
                <p className="text-xs text-zinc-400">
                  O painel de Vendas, Estoque e Relatórios Financeiros é exclusivo para gestores de arenas. Se você
                  possui uma arena, altere seu tipo de conta no seu Perfil.
                </p>
                <button
                  type="button"
                  onClick={() => handleSelectTab("perfil")}
                  className="px-5 py-2.5 bg-amber-400 text-emerald-950 font-black text-xs rounded-xl shadow-lg transition hover:bg-amber-300 cursor-pointer"
                >
                  Ir para Meu Perfil / Alterar Tipo de Conta
                </button>
              </div>
            )}
          </motion.div>
        }
      />
      <Route
        path={ROUTES.ARENA_VENDAS}
        element={
          <motion.div
            key="arena_vendas"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            {userProfile.accountType === "dono_arena" ? (
              !canAccessOperational ? (
                <OperationalModuleBlockedView
                  arenaName={arenaData?.name || arenaData?.arenaName || userProfile.arenaName || "Sua Arena"}
                  arenaId={canonicalArenaId}
                  ownerUid={user?.uid || userProfile.id}
                  onBack={() => handleSelectTab("hub")}
                  onToast={showToast}
                />
              ) : (
                <VendasFinanceiroPanel userProfile={userProfile} courts={courts} onToast={showToast} />
              )
            ) : (
              <div className="p-8 text-center bg-zinc-950 border border-zinc-800 rounded-3xl max-w-lg mx-auto my-12 space-y-4">
                <div className="w-16 h-16 bg-amber-400/10 text-amber-400 rounded-full flex items-center justify-center mx-auto font-bold text-2xl border border-amber-400/20">
                  🔒
                </div>
                <h3 className="text-xl font-display font-black text-white">
                  Acesso Exclusivo para Donos de Arena
                </h3>
                <p className="text-xs text-zinc-400">
                  O painel de Vendas, Estoque e Relatórios Financeiros é exclusivo para gestores de arenas. Se você
                  possui uma arena, altere seu tipo de conta no seu Perfil.
                </p>
                <button
                  type="button"
                  onClick={() => handleSelectTab("perfil")}
                  className="px-5 py-2.5 bg-amber-400 text-emerald-950 font-black text-xs rounded-xl shadow-lg transition hover:bg-amber-300 cursor-pointer"
                >
                  Ir para Meu Perfil / Alterar Tipo de Conta
                </button>
              </div>
            )}
          </motion.div>
        }
      />

      {/* AGENDAMENTOS DO ATLETA */}
      <Route
        path={ROUTES.MEUS_AGENDAMENTOS}
        element={
          <motion.div
            key="meus_agendamentos"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            <AthleteBookingsHistory />
          </motion.div>
        }
      />
      <Route
        path={ROUTES.AGENDAMENTOS}
        element={
          <motion.div
            key="agendamentos"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            <AthleteBookingsHistory />
          </motion.div>
        }
      />

      {/* MEU PERFIL */}
      <Route
        path={ROUTES.PERFIL}
        element={
          <motion.div
            key="perfil"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            <UserProfileManager
              userProfile={userProfile}
              courts={courts}
              onUpdateProfile={(updated) => setUserProfile(updated)}
              onLogout={handleGoogleLogout}
              onBack={() => handleSelectTab("hub")}
            />
          </motion.div>
        }
      />

      {/* REDIRECIONAMENTO DE ROTA LEGADA DUPLAS PARA SORTEIO RACHA */}
      <Route
        path={ROUTES.DUPLAS}
        element={<Navigate to={ROUTES.JOGO} replace />}
      />

      {/* TORNEIOS & PUBLIC PORTAL DEEP LINKS */}
      <Route
        path={ROUTES.TORNEIOS}
        element={
          <motion.div
            key="torneios"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            <TorneioManager
              duplas={duplas}
              torneios={torneios}
              onSaveTorneio={handleSaveTorneio}
              onDeleteTorneio={handleDeleteTorneio}
              userId={user?.uid || null}
              userProfile={userProfile}
              onUpdateProfile={setUserProfile}
              onAddDupla={handleAddDupla}
              isLoggedIn={isLoggedIn}
              onOpenGoogleAuth={() => setShowGoogleAuthModal(true)}
            />
          </motion.div>
        }
      />
      <Route
        path={ROUTES.TORNEIO_DETAILS}
        element={
          <motion.div
            key="torneio_details"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            <TournamentRouteWrapper
              duplas={duplas}
              torneios={torneios}
              handleSaveTorneio={handleSaveTorneio}
              handleDeleteTorneio={handleDeleteTorneio}
              user={user}
              userProfile={userProfile}
              setUserProfile={setUserProfile}
              handleAddDupla={handleAddDupla}
              isLoggedIn={isLoggedIn}
              setShowGoogleAuthModal={setShowGoogleAuthModal}
            />
          </motion.div>
        }
      />
      <Route
        path={ROUTES.INSCRICAO_DETAILS}
        element={
          <motion.div
            key="inscricao_details"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            <TournamentRouteWrapper
              duplas={duplas}
              torneios={torneios}
              handleSaveTorneio={handleSaveTorneio}
              handleDeleteTorneio={handleDeleteTorneio}
              user={user}
              userProfile={userProfile}
              setUserProfile={setUserProfile}
              handleAddDupla={handleAddDupla}
              isLoggedIn={isLoggedIn}
              setShowGoogleAuthModal={setShowGoogleAuthModal}
            />
          </motion.div>
        }
      />
      {/* ÁREA OPERACIONAL EXCLUSIVA DO AJUDANTE */}
      <Route
        path={ROUTES.ASSISTANT_AREA}
        element={
          <motion.div
            key="assistant_area"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            <TournamentAssistantArea
              duplas={duplas}
              torneios={torneios}
              onSaveTorneio={handleSaveTorneio}
              onAddDupla={handleAddDupla}
              isLoggedIn={isLoggedIn}
              onOpenGoogleAuth={() => setShowGoogleAuthModal(true)}
            />
          </motion.div>
        }
      />
      <Route
        path={ROUTES.AJUDANTE_AREA}
        element={
          <motion.div
            key="ajudante_area"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            <TournamentAssistantArea
              duplas={duplas}
              torneios={torneios}
              onSaveTorneio={handleSaveTorneio}
              onAddDupla={handleAddDupla}
              isLoggedIn={isLoggedIn}
              onOpenGoogleAuth={() => setShowGoogleAuthModal(true)}
            />
          </motion.div>
        }
      />
      <Route
        path={ROUTES.PORTAL}
        element={
          <motion.div
            key="portal"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            <PublicPortal
              duplas={duplas}
              torneios={torneios}
              onSaveTorneio={handleSaveTorneio}
              onAddDupla={handleAddDupla}
              userId={user?.uid || null}
              isLoggedIn={isLoggedIn}
              onOpenGoogleAuth={() => setShowGoogleAuthModal(true)}
            />
          </motion.div>
        }
      />

      {/* STATS & HISTORICO */}
      <Route
        path={ROUTES.STATS}
        element={
          <motion.div
            key="stats"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            <StatsPanel
              historico={historico}
              duplas={globalDuplas}
              onClearHistory={handleClearHistory}
              onExportJSON={exportJSON}
              onExportExcel={exportExcel}
            />
          </motion.div>
        }
      />
      <Route
        path={ROUTES.HISTORICO}
        element={
          <motion.div
            key="historico"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            <MatchHistory
              sessoes={sessoes}
              historicoAtual={historico}
              duplasAtuais={globalDuplas}
              torneios={torneios}
              onReactivateSession={handleReactivateSession}
              onDeleteSession={handleDeleteSession}
              onClearAllSessions={handleClearAllSessions}
              onClearHistoricoAtual={handleClearHistory}
              onExportJSON={exportJSON}
              onExportExcel={exportExcel}
            />
          </motion.div>
        }
      />

      {/* PAINEL SUPER ADMIN */}
      <Route
        path={ROUTES.ADMIN}
        element={
          <motion.div
            key="admin"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            {!isSuperAdminAuth ? (
              <SuperAdminLoginGate onAuthenticated={() => setIsSuperAdminAuth(true)} />
            ) : (
              <AdminPanel
                torneios={torneios}
                duplas={duplas}
                onSaveTorneio={handleSaveTorneio}
                onDeleteTorneio={handleDeleteTorneio}
                onAddDupla={handleAddDupla}
                adminTab={adminSubTab}
                onSelectAdminTab={setAdminSubTab}
              />
            )}
          </motion.div>
        }
      />
      <Route
        path={ROUTES.ADMIN_SUBTAB}
        element={
          <motion.div
            key="admin_subtab"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
          >
            <AdminRouteWrapper
              torneios={torneios}
              duplas={duplas}
              handleSaveTorneio={handleSaveTorneio}
              handleDeleteTorneio={handleDeleteTorneio}
              handleAddDupla={handleAddDupla}
              isSuperAdminAuth={isSuperAdminAuth}
              setIsSuperAdminAuth={setIsSuperAdminAuth}
              setAdminSubTab={(subtab: any) => setAdminSubTab(subtab)}
            />
          </motion.div>
        }
      />

      {/* 404 NOT FOUND FALLBACK */}
      <Route
        path="*"
        element={
          <motion.div
            key="not_found"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            className="text-center py-16 px-4 max-w-md mx-auto space-y-5"
          >
            <div className="w-20 h-20 bg-amber-400/10 text-amber-400 rounded-3xl flex items-center justify-center mx-auto text-4xl font-black border border-amber-400/20 shadow-xl">
              404
            </div>
            <h2 className="text-2xl font-black text-white">Página Não Encontrada</h2>
            <p className="text-xs text-zinc-400">
              A página ou endereço que você tentou acessar não existe ou foi movido.
            </p>
            <button
              type="button"
              onClick={() => handleSelectTab("hub")}
              className="px-6 py-3 bg-amber-400 hover:bg-amber-300 text-emerald-950 font-black text-xs rounded-xl shadow-lg transition cursor-pointer"
            >
              Voltar ao HUB Principal
            </button>
          </motion.div>
        }
      />
    </Routes>
    </Suspense>
  );
};
